import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';
import { Adjustment, AdjustmentApplication, AdjustmentCategory, AdjustmentReasonCategory, AdjustmentSite } from '../models/response-models/adjustment';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { AdjustmentReason } from '../models/response-models/adjustment-reason';
import { Vat } from '../models/response-models/vat';
import { MultiSelectionItem } from '../models/application-models/multi-selection-item';

@Injectable({
  providedIn: 'root'
})
export class AdjustmentService {

  constructor(
    private http: HttpClient
  ) { }

  getAdjustments(returnManualDiscounts?: Boolean): Observable<Adjustment[]>{

    return this.http.get<Adjustment[]>(environment.serverUrl + "/adjustments");
  }

  getAdjustmentsWithManualDiscount(): Observable<Adjustment[]>{

    return this.http.get<Adjustment[]>(environment.serverUrl + "/adjustments/includingManualDiscount");
  }

  getAdjustmentByID(adjustmentID: string): Observable<Adjustment>{
    return this.http.get<Adjustment>(environment.serverUrl + `/adjustments/${adjustmentID}`);
  }

  createAdjustment(form: UntypedFormGroup): Observable<Adjustment>{
    let options = {
      adjustmentApplicationID: form.controls.adjustmentApplication.value.id,
      adjustmentCategoryID: form.controls.adjustmentCategory.value.id,
      adjustmentSetting: {
        applyAfterOrderBalance: false,
        autoApply: form.controls.autoApply.value,
        displayReasonOnReceipt: form.controls.displayReason.value,
        id: form.controls.id.value,
        includeAdjustmentInPrice: form.controls.serviceChargePriceInclusive.value,
        promptForReason: form.controls.promptForReason.value,
        applyToListValues: form.controls.applyToListValues.value,
        applyToNetValues: form.controls.applyToNetValues.value,
        includeInVAT: form.controls.includeInVAT.value,
        chargePerCover: form.controls.chargePerCover.value,
        allowOverLoadedDiscount: form.controls.allowOverLoadedDiscount.value,
        allowMultipleDiscounts: form.controls.allowMultipleDiscounts.value
      },
      amount: form.controls.amount.value,
      customerFacingName: form.controls.customerFacingName.value,
      increasesPrice: form.controls.increasesPrice.value,
      id: form.controls.id.value,
      isActive: form.controls.isActive.value,
      displayOnPOS: form.controls.displayOnPOS.value,
      name: form.controls.name.value,
      percentage: form.controls.percentage.value
    }

    return this.http.post<Adjustment>(environment.serverUrl + `/adjustments`, options);
  }

  editAdjustment(form: UntypedFormGroup): Observable<Adjustment>{
    let options = {
      adjustmentApplicationID: form.controls.adjustmentApplication.value.id,
      adjustmentCategoryID: form.controls.adjustmentCategory.value.id,
      adjustmentSetting: {
        applyAfterOrderBalance: false,
        autoApply: form.controls.autoApply.value,
        displayReasonOnReceipt: form.controls.displayReason.value,
        applyToListValues: form.controls.applyToListValues.value,
        applyToNetValues: form.controls.applyToNetValues.value,
        includeInVAT: form.controls.includeInVAT.value,
        chargePerCover: form.controls.chargePerCover.value,
        id: form.controls.id.value,
        includeAdjustmentInPrice: form.controls.serviceChargePriceInclusive.value,
        promptForReason: form.controls.promptForReason.value,
        allowOverLoadedDiscount: form.controls.allowOverLoadedDiscount.value,
        allowMultipleDiscounts: form.controls.allowMultipleDiscounts.value
      },
      amount: form.controls.amount.value,
      customerFacingName: form.controls.customerFacingName.value,
      increasesPrice: form.controls.increasesPrice.value,
      id: form.controls.id.value,
      isActive: form.controls.isActive.value,
      displayOnPOS: form.controls.displayOnPOS.value,
      name: form.controls.name.value,
      percentage: form.controls.percentage.value
    }

    return this.http.put<Adjustment>(environment.serverUrl + `/adjustments`, options);
  }

  editAdjustmentSite(form: UntypedFormGroup, adjustment: Adjustment): Observable<Adjustment>{
    const adjustmentSites: UntypedFormArray  = <UntypedFormArray>form.controls.adjustmentSites;

    let newAdjustmentSites: AdjustmentSite[] = [];
    for(let adjustmentSiteGroup of adjustmentSites.controls as Array<UntypedFormGroup>){
      if (adjustmentSiteGroup.dirty) {
        let newAdjustmentSite: AdjustmentSite = {
          langAdjustmentName: adjustmentSiteGroup.controls.langAdjustmentName.value,
          siteID: adjustmentSiteGroup.controls.siteID.value
        }
        newAdjustmentSites.push(newAdjustmentSite);
      }
    }

    return this.http.patch<Adjustment>(environment.serverUrl + `/adjustments/${adjustment.id}/sites`, newAdjustmentSites);
  }

  getAdjustmentCategories(): Observable<AdjustmentCategory[]>{
    return this.http.get<AdjustmentCategory[]>(environment.serverUrl + "/adjustmentCategories");
  }

  getAdjustmentReasonCategories(): Observable<AdjustmentReasonCategory[]>{
    return this.http.get<AdjustmentReasonCategory[]>(environment.serverUrl + "/adjustments/adjustmentReasons/adjustmentCategories");
  }

  getAdjustmentApplications(): Observable<AdjustmentApplication[]>{
    return this.http.get<AdjustmentApplication[]>(environment.serverUrl + "/adjustmentApplications");
  }

  removeAdjustmentByID(adjustmentID: string){
    const options: any = {
      body: {
        id: adjustmentID
      }
    }

    return this.http.delete<any>(environment.serverUrl + `/adjustments`, options);
  }

  getAdjustmentReasons(): Observable<AdjustmentReason[]>{
    return this.http.get<AdjustmentReason[]>(environment.serverUrl + "/adjustments/adjustmentReasons");
  }

  getAdjustmentReasonByID(adjustmentReasonID: string): Observable<AdjustmentReason>{
    return this.http.get<AdjustmentReason>(environment.serverUrl + `/adjustments/adjustmentReasons/${adjustmentReasonID}`);
  }

  createAdjustmentReason(form: UntypedFormGroup): Observable<AdjustmentReason>{
    let options = {
      id: form.controls.id.value,
      reason: form.controls.reason.value,
      requiresCustomReason: form.controls.requiresCustomReason.value
    }

    return this.http.post<AdjustmentReason>(environment.serverUrl + `/adjustments/adjustmentReasons`, options);
  }

  editAdjustmentReason(form: UntypedFormGroup): Observable<AdjustmentReason>{
    let options = {
      id: form.controls.id.value,
      reason: form.controls.reason.value,
      requiresCustomReason: form.controls.requiresCustomReason.value
    }

    return this.http.put<AdjustmentReason>(environment.serverUrl + `/adjustments/adjustmentReasons`, options);
  }

  removeAdjustmentReasonByID(adjustmentReasonID: string){
    const options: any = {
      body: {
        id: adjustmentReasonID
      }
    }

    return this.http.delete<any>(environment.serverUrl + `/adjustments/adjustmentReasons`, options);
  }

  getVAT(): Observable<Vat[]>{
    return this.http.get<Vat[]>(environment.serverUrl + "/adjustments/VAT");
  }

  addSiteRelationship(adjustmentID: string, siteItem: MultiSelectionItem){
    let options: any = {
      id: siteItem.id
    }

    return this.http.post<Adjustment>(environment.serverUrl + `/adjustments/${adjustmentID}/sites`, options);
  }

  removeSiteRelationship(adjustmentID: string, siteItem: MultiSelectionItem){
    let options: any = {
      body: {
        id: siteItem.id
      }
    };

    return this.http.delete<any>(environment.serverUrl + `/adjustments/${adjustmentID}/sites`, options);
  }

  addOrderTypeRelationship(adjustmentID: string, orderTypeItem: MultiSelectionItem){
    let options: any = {
      id: orderTypeItem.id
    }

    return this.http.post<Adjustment>(environment.serverUrl + `/adjustments/${adjustmentID}/orderTypes`, options);
  }

  removeOrderTypeRelationship(adjustmentID: string, orderTypeItem: MultiSelectionItem){
    let options: any = {
      body: {
        id: orderTypeItem.id
      }
    };

    return this.http.delete<any>(environment.serverUrl + `/adjustments/${adjustmentID}/orderTypes`, options);
  }

  addScheduleRelationship(adjustmentID: string, scheduleItem: MultiSelectionItem){
    let options: any = {
      id: scheduleItem.id
    }

    return this.http.post<Adjustment>(environment.serverUrl + `/adjustments/${adjustmentID}/schedules`, options);
  }

  removeScheduleRelationship(adjustmentID: string, scheduleItem: MultiSelectionItem){
    let options: any = {
      body: {
        id: scheduleItem.id
      }
    };

    return this.http.delete<any>(environment.serverUrl + `/adjustments/${adjustmentID}/schedules`, options);
  }

  addAdjustmentReasonCategoryRelationship(adjustmentReasonID: string, adjustmentReasonCategoryItem: MultiSelectionItem){
    let options: any = {
      id: adjustmentReasonCategoryItem.id
    }

    return this.http.post<AdjustmentReasonCategory>(environment.serverUrl + `/adjustments/adjustmentReasons/${adjustmentReasonID}/categories`, options);
  }

  removeAdjustmentReasonCategoryRelationship(adjustmentReasonID: string, adjustmentReasonCategoryItem: MultiSelectionItem){
    let options: any = {
      body: {
        id: adjustmentReasonCategoryItem.id
      }
    };

    return this.http.delete<any>(environment.serverUrl + `/adjustments/adjustmentReasons/${adjustmentReasonID}/categories`, options);
  }


  addCategoryInclusionAndExceptions(adjustmentID: string, inclusionCategoryArray: any[]): Observable<any>{
    return this.http.post<any>(environment.serverUrl + `/adjustments/${adjustmentID}/productCategoriesCollection`, inclusionCategoryArray);
  }

  addProductInclusionAndExceptions(adjustmentID: string, inclusionProductArray: any[]): Observable<any>{
    return this.http.post<any>(environment.serverUrl + `/adjustments/${adjustmentID}/productsCollection`, inclusionProductArray);
  }

  removeCategoryInclusionAndExceptions(adjustmentID: string, inclusionCategoryArray: any[]): Observable<any>{
    let options: any = {
      body: inclusionCategoryArray
    };
    return this.http.delete<any>(environment.serverUrl + `/adjustments/${adjustmentID}/productCategoriesCollection`, options);
  }

  removeProductInclusionAndExceptions(adjustmentID: string, inclusionProductArray: any[]): Observable<any>{
    let options: any = {
      body: inclusionProductArray
    };
    return this.http.delete<any>(environment.serverUrl + `/adjustments/${adjustmentID}/productsCollection`, options);
  }
}
